import { useLoadIElements } from "@/hooks/use-load-ielements";
import { useAppSelector } from "@/store/store-hooks";
import { LOADING, MaybeLoading } from "@faro-lotv/foundation";
import { IElementImg360, IElementType } from "@faro-lotv/ielement-types";
import {
  GetIElementsParams,
  ResponseSize,
  RevisionScanEntity,
} from "@faro-lotv/service-wires";
import { useEffect, useMemo, useState } from "react";
import { selectImg360ForScanEntity } from "../store/revision/revision-scan-selectors";

/**
 * @param scanEntity The entity to load the img360 for.
 * @param shouldLoad Setting this to `true` will initiate the loading of the img360.
 * @returns The img360 of the scan if loaded, `undefined` if it doesn't exist
 *   or `LOADING` if the img360 is being loaded.
 */
export function useLazyLoadRevisionImg360(
  scanEntity: RevisionScanEntity | undefined,
  shouldLoad: boolean,
): MaybeLoading<IElementImg360> {
  const img360 = useAppSelector(selectImg360ForScanEntity(scanEntity));
  const [loadingInitiated, setLoadingInitiated] = useState(shouldLoad);

  useEffect(() => {
    if (shouldLoad) {
      setLoadingInitiated(true);
    }
  }, [shouldLoad]);

  const requests = useMemo(() => {
    if (!scanEntity || !loadingInitiated) return [];
    return [
      {
        parentIds: [scanEntity.id],
        types: [IElementType.img360],
        expectedResponseSize: ResponseSize.small,
      },
      // The pano world pose is only complete once the whole ancestor chain is in
      // the store, otherwise the panorama is rendered with a wrong orientation.
      {
        descendantIds: [scanEntity.id],
        expectedResponseSize: ResponseSize.small,
      },
    ] satisfies GetIElementsParams[];
  }, [scanEntity, loadingInitiated]);

  const isLoading = useLoadIElements(requests);

  return isLoading ? LOADING : img360;
}
